import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { auth, db } from "@/lib/firebase";
import { doc, getDoc, setDoc } from 'firebase/firestore';
import {
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  signOut as firebaseSignOut,
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
  GoogleAuthProvider,
} from 'firebase/auth';
import { log } from '@/utils/logger';
import { fetchIsAdminDetailed, type AdminDiagnostics } from '@/lib/admin';
import { isDebugEnabled } from '@/lib/debugFlag';
import { setRuntimeIsAdmin, setRuntimeAuthReady } from '@/debug/runtimeInfo';

const googleProvider = new GoogleAuthProvider();

interface AuthContextValue {
  user: User | null;
  currentUser: User | null;
  login: () => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  register: (email: string, password: string) => Promise<any>;
  logout: () => Promise<void>;
  sendPasswordResetEmail: (email: string) => Promise<void>;
  loading: boolean;
  error: string | null;
  isAdmin: boolean;
  role: string | null;
  adminDiag: AdminDiagnostics | null;
  authReady: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [role, setRole] = useState<string | null>(null);
  const [adminDiag, setAdminDiag] = useState<AdminDiagnostics | null>(null);
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, currentUser => {
      setUser(currentUser);
      // Set auth ready on every auth state change, not just initial
      setAuthReady(true);
      setRuntimeAuthReady(true);
      if (currentUser) {
        (async () => {
          const uid = currentUser.uid;
          try {
            const userRef = doc(db, 'users', uid);
            const snap = await getDoc(userRef);
            if (!snap.exists()) {
              await setDoc(userRef, {
                uid,
                email: currentUser.email,
                displayName: currentUser.displayName,
                photoURL: currentUser.photoURL,
                role: 'user',
              });
              setRole('user');
            } else {
              const data = snap.data() as any;
              if (data.suspended) {
                await firebaseSignOut(auth);
                setUser(null);
                setRole(null);
                setIsAdmin(false);
                setRuntimeIsAdmin(false);
                setError('Account suspended');
                return;
              }
              setRole(data.role);
            }
          } catch {
            if (auth.currentUser?.uid !== uid) return;
            setRole(null);
            setIsAdmin(false);
            setRuntimeIsAdmin(false);
          }
        })();
      } else {
        setRole(null);
        setIsAdmin(false);
        setAdminDiag(null);
        setRuntimeIsAdmin(false);
      }
    });
    return () => {
      try {
        unsubscribe();
      } catch (e) {
        console.warn('[AuthProvider] non-fatal unsubscribe error', e);
      }
      // Don't reset authReady on cleanup - it should stay true once initialized
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.uid) {
        setIsAdmin(false);
        setAdminDiag(null);
        setRuntimeIsAdmin(false);
        return;
      }
      const diag = await fetchIsAdminDetailed(db, user.uid);
      if (!cancelled) {
        setIsAdmin(diag.isAdmin);
        setAdminDiag(diag);
        setRuntimeIsAdmin(diag.isAdmin);
        console.log('[AuthProvider] admin diagnostics', diag);
        if (isDebugEnabled()) {
          console.debug('[auth]', { uid: user.uid, isAdmin: diag.isAdmin, diag });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  const login = async () => {
    setLoading(true);
    setError(null);
    try {
      const host = new URLSearchParams(window.location.search).get('host');
      if (host === 'office') {
        await signInWithRedirect(auth, googleProvider);
      } else {
        await signInWithPopup(auth, googleProvider);
      }
      await log.info('auth_signin_google', { message: 'User signed in with Google' });
    } catch (err: any) {
      setError(err.message);
      await log.error('auth_signin_failed', { message: err.message, method: 'google' });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    setLoading(true);
    setError(null);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      await log.info('auth_signin_email', { message: 'User signed in with email/password' });
    } catch (err: any) {
      setError(err.message);
      await log.error('auth_signin_failed', { message: err.message, method: 'email' });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const register = async (email: string, password: string) => {
    setLoading(true);
    setError(null);
    try {
      await createUserWithEmailAndPassword(auth, email, password);
      await log.info('auth_register', { message: 'User registered with email/password', method: 'email' });
    } catch (err: any) {
      setError(err.message);
      await log.error('auth_register_failed', { message: err.message, method: 'email' });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('[AuthContext] Starting logout...');
      console.log('[AuthContext] Current user before logout:', auth.currentUser?.email || auth.currentUser?.uid || 'none');
      
      // Log BEFORE signing out (while user is still authenticated)
      // Wait for the log to complete, but catch errors so they don't block signout
      try {
        await log.info('auth_signout', { message: 'User signed out' });
      } catch (logError) {
        console.error('Failed to log signout:', logError);
      }
      
      // Sign out
      await firebaseSignOut(auth);
      console.log('[AuthContext] Firebase signOut completed');
      console.log('[AuthContext] Current user after logout:', auth.currentUser?.email || auth.currentUser?.uid || 'none');
      
    } catch (err: any) {
      setError(err.message);
      console.error('[AuthContext] Logout error:', err);
      // Try to log the error, but don't block on it
      log.error('auth_signout_failed', { message: err.message }).catch(console.error);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const sendPasswordResetEmail = async (email: string) => {
    try {
      console.log('[Password Reset] 🔐 Requesting reset for:', email);
      console.log('[Password Reset] ℹ️  Note: Firebase always returns success (even if email doesn\'t exist) to prevent email enumeration attacks');
      await firebaseSendPasswordResetEmail(auth, email);
      console.log('[Password Reset] ✓ Request completed successfully');
      console.log('[Password Reset] 📧 If this email exists in Firebase, a reset link was sent');
      console.log('[Password Reset] ⚠️  If email doesn\'t exist, NO email was sent (but we don\'t reveal that for security)');
      await log.info('password_reset_requested', { message: 'Password reset email sent', email });
    } catch (err: any) {
      console.error('[Password Reset] ✗ Request failed with error:', err.message);
      console.error('[Password Reset] Full error:', err);
      await log.error('password_reset_failed', { message: err.message, email });
      throw err;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        currentUser: user,
        login,
        signIn,
        register,
        logout,
        sendPasswordResetEmail,
        loading,
        error,
        isAdmin,
        role,
        adminDiag,
        authReady,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => useContext(AuthContext)!;

export function useAuth() {
  const ctx = useAuthContext();
  return { user: ctx.currentUser, isAdmin: ctx.isAdmin, authReady: ctx.authReady };
}

